import { HIGHLIGHT_FILL_HANDLE_CLASS } from '@databases/highlights/constants';

import { type GridCoord, type GridPosition } from '../../../machine/state/types';

import { isPointerOnDatabaseScrollableNativeScrollbar } from './native-scrollbar-hit';

export type ClickTarget =
  | {
      type: 'cell';
      position: GridPosition;
      /**
       * Frozen cells suppress horizontal drag auto-scroll while preserving
       * vertical auto-scroll.
       */
      horizontalAutoScroll?: 'disabled';
    }
  | { type: 'row'; row: GridCoord; source: 'row-handle' | 'row-checkbox' }
  | { type: 'column'; col: GridCoord }
  | { type: 'headerCheckbox' }
  | { type: 'fillHandle' }
  | { type: 'outside' }
  | { type: 'none' };

const HEADER_CHECKBOX_SELECTOR = '#selections-header-checkbox';
const ROW_BUTTON_SELECTOR = '[data-testid="row-select-button"]';
const ROW_CHECKBOX_SELECTOR = '.selections-entry-checkbox';
const FILL_HANDLE_SELECTOR = `.${HIGHLIGHT_FILL_HANDLE_CLASS}`;
const DATABASE_BOE_PROMPT_INPUT_SELECTOR =
  '[data-testid="database-blank-object-experience"] [contenteditable]:not([contenteditable="false"]), [data-testid="database-blank-object-experience"] .ProseMirror';
/**
 * Classifies a pointer position for Databases table selection. Coordinates are viewport
 * (e.g. `clientX` / `clientY`). Scrollbar hits on `#scrollable-inner-container` use
 * `isPointerOnDatabaseScrollableNativeScrollbar` so they map to `none` instead of
 * `outside` (avoids `deselectAll` on scrollbar interaction); see `native-scrollbar-hit.ts` for
 * scope and `data-canvas-target` / overlay limits.
 */
export function getClickTarget(x: number, y: number): ClickTarget {
  const el = document.elementFromPoint(x, y);

  if (!el) {
    return { type: 'none' };
  }

  const headerCheckbox = el.closest(HEADER_CHECKBOX_SELECTOR);
  if (headerCheckbox) {
    return { type: 'headerCheckbox' };
  }

  const rowSelectButtonEl = el.closest(ROW_BUTTON_SELECTOR);
  if (rowSelectButtonEl) {
    const rowEl = rowSelectButtonEl.closest('tr[data-row-index]');

    if (rowEl) {
      const rowIndex = rowEl.getAttribute('data-row-index');

      if (rowIndex !== null) {
        return {
          type: 'row',
          row: {
            index: parseInt(rowIndex, 10)
          },
          source: 'row-handle'
        };
      }
    }
  }

  const rowCheckboxEl = el.closest(ROW_CHECKBOX_SELECTOR);
  if (rowCheckboxEl) {
    const rowIndex = rowCheckboxEl.getAttribute('data-row-index');

    if (rowIndex !== null) {
      return {
        type: 'row',
        row: {
          index: parseInt(rowIndex, 10)
        },
        source: 'row-checkbox'
      };
    }
  }

  const cellEl = el.closest('[data-row-index][data-col-index]');

  if (cellEl) {
    const rowIndex = cellEl.getAttribute('data-row-index');
    const colIndex = cellEl.getAttribute('data-col-index');

    if (rowIndex !== null && colIndex !== null) {
      return {
        type: 'cell',
        horizontalAutoScroll:
          cellEl.getAttribute('data-dbs-pinned-leading') === 'true'
            ? 'disabled'
            : undefined,
        position: {
          row: { index: parseInt(rowIndex, 10) },
          col: { index: parseInt(colIndex, 10) }
        }
      };
    }
  }

  const fillHandleEl = el.closest(FILL_HANDLE_SELECTOR);

  if (fillHandleEl) {
    return { type: 'fillHandle' };
  }

  // Clicks in the Databases layout shell but not on cells/controls: empty table chrome → outside
  // (deselect). Native *classic* scrollbar on #scrollable-inner-container is none (see native-scrollbar-hit).
  const isInsideLayoutShell = el.closest(
    '#scrollable-inner-container, [data-testid="layout-view--wrapper"], #layout-container'
  );

  const isInteractive = el.closest(
    'table, button, [role="button"], input, textarea, a, [data-testid="database-value-view-container"], [data-testid="layout-right-side-panel"]'
  );
  const isDatabaseBoePromptInput = el.closest(DATABASE_BOE_PROMPT_INPUT_SELECTOR);

  if (isInsideLayoutShell && !isInteractive && !isDatabaseBoePromptInput) {
    if (isPointerOnDatabaseScrollableNativeScrollbar(el, x, y)) {
      return { type: 'none' };
    }

    return { type: 'outside' };
  }

  return { type: 'none' };
}
